import { Controller } from "@hotwired/stimulus";

/**
 * 🔴 `AI-02` / §5.3 — LA BORNE DE POIDS, CÔTÉ NAVIGATEUR (lot L34).
 *
 * > §5.3 · « limite cumulée 10 Mio », et `AI-02` exige le refus « frontend ET backend ».
 *
 * ## ⛔ Ce qui existait, et ce que ça ne faisait PAS
 *
 * Le champ portait `accept="application/pdf,image/jpeg,…"`. C'est une commodité de boîte
 * de dialogue : elle ne PÈSE rien, elle ne refuse rien à un glisser-déposer, et elle ne
 * dit rien du total. Un auteur pouvait donc téléverser 40 Mio, attendre le transfert
 * entier sur une connexion mobile, et n'apprendre qu'ensuite que c'était refusé — au
 * tarif des données, c'est exactement ce que le corpus refuse de faire payer.
 *
 * ## ⚠️ LE TOTAL CONSERVÉ, pas la sélection courante
 *
 * §5.3 : « Vérifier le total de tous les fichiers sources CONSERVÉS dans la demande, pas
 * seulement les derniers ajoutés. » Le poids déjà attaché voyage donc en `data-kept-bytes`,
 * rendu par le serveur — le navigateur ne le devine pas.
 *
 * ## ⛔ CE CONTRÔLEUR NE REMPLACE PAS LA BORNE SERVEUR
 *
 * Il l'ANNONCE plus tôt. `Content::AiSources` refuse pour de bon, sur toutes les voies —
 * requête forgée, API mobile, import. Les deux, jamais l'une seule : c'est la doctrine que
 * `AI-02` porte depuis le lot L12-b, et un contrôle client qui se croirait suffisant serait
 * la faute que §15.2 nomme (« ne pas afficher un statut qu'aucun contrôle n'a produit »).
 */
export default class extends Controller {
  static targets = ["input", "notice"];
  static values = {
    keptBytes: Number, maxBytes: Number, maxFiles: Number,
    messageBytes: String, messageFiles: String,
  };

  connect() {
    this.clear();
  }

  // ⚠️ **On pèse à la SÉLECTION**, pas à l'envoi : prévenir après que l'auteur a cliqué
  // « Envoyer » lui ferait perdre le geste, et sur un téléphone la sélection est le seul
  // moment où il a encore les fichiers sous la main.
  weigh() {
    const files = Array.from(this.inputTarget.files || []);
    const total = files.reduce((sum, file) => sum + file.size, this.keptBytesValue);
    const count = files.length + this.keptFilesCount();

    // ⚠️ **Deux fautes, DEUX phrases.** Un message unique à trou (« trop de %{fault} »)
    // aurait produit une langue que personne ne parle, et interdit de dire à l'auteur ce
    // qu'il peut FAIRE — retirer un fichier n'est pas alléger un fichier.
    const message = this.messageFor(total, count);
    if (message === null) {
      this.clear();
      return;
    }

    // ⛔ **Le champ est VIDÉ.** Laisser la sélection en place après l'avoir annoncée
    // refusée enverrait quand même les octets au premier envoi du formulaire — le message
    // aurait menti.
    this.inputTarget.value = "";
    this.announce(message);
  }

  messageFor(total, count) {
    if (count > this.maxFilesValue) return this.messageFilesValue;
    if (total > this.maxBytesValue) return this.messageBytesValue;
    return null;
  }

  keptFilesCount() {
    return Number(this.element.dataset.keptFiles || 0);
  }

  announce(message) {
    if (!this.hasNoticeTarget) return;

    this.noticeTarget.textContent = message;
    this.noticeTarget.hidden = false;
  }

  clear() {
    if (!this.hasNoticeTarget) return;

    this.noticeTarget.textContent = "";
    this.noticeTarget.hidden = true;
  }
}
